import { useCallback } from "react";
import Swal from "sweetalert2";
import { useLocation } from "react-router-dom";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { ClosingControlService } from "../../../../services/closing-control.service";
import {
  ClosingControlState,
  ClosingHeaderButton,
} from "../../../../types/closing-control.type";
import { useProject } from "../../../../contexts/project.context";
import { TabActive } from "../../../../types/project.type";
import { closingApprovalTransitionRef } from "../../../../utils/closingApprovalTransition";
import { countUndefinedSuppliers } from "../../../../utils/supplier";
function getClosingStageButtonLabel(button: ClosingHeaderButton): string {
  return button === "PRE_CLOSING" ? "Pré Fechamento" : "Devolver";
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const apiError = error as {
    response?: { data?: { message?: string } };
  };
  return apiError?.response?.data?.message || fallback;
}

export function useClosingStageActions() {
  const { tabActive } = useProject();
  const location = useLocation();
  const projectId = new URLSearchParams(location.search).get("projectId");
  const {
    closingControl,
    isLoadingClosingControl,
    isFetchingClosingControl,
    applyClosingControlState,
    refetchClosingControl,
    activeBudget,
  } = useBudget();

  const activeBudgetId = activeBudget?._id;

  const closingStageButton = closingControl?.headerButton ?? null;
  const canEditClosing = closingControl?.canEditClosing ?? false;
  const isClosingTab = tabActive === TabActive.APPROVED;
  const hasClosingControlData = closingControl != null;
  const isClosingControlPending =
    isClosingTab &&
    !!projectId &&
    !hasClosingControlData &&
    (isLoadingClosingControl || isFetchingClosingControl);

  const showClosingStageAction =
    isClosingTab &&
    !!projectId &&
    hasClosingControlData &&
    closingStageButton !== null &&
    !closingApprovalTransitionRef.current;

  const runTransition = useCallback(
    async (request: () => Promise<ClosingControlState>) => {
      if (!projectId) return false;

      try {
        const nextState = await request();
        applyClosingControlState(nextState);
        return true;
      } catch (error) {
        Swal.fire({
          title: "Não foi possível concluir a ação",
          text: getApiErrorMessage(
            error,
            "Verifique suas permissões e o status atual do fechamento.",
          ),
          icon: "error",
        });
        await refetchClosingControl();
        return false;
      }
    },
    [applyClosingControlState, projectId, refetchClosingControl],
  );

  // POST /pre-closing somente no clique explícito do usuário no footer.
  const sendToPreClosing = useCallback(() => {
    // O Pré Fechamento agora é POR ORÇAMENTO: valida "Fornecedor a Definir"
    // apenas no orçamento selecionado (activeBudget) e envia somente ele.
    const latestVersion = activeBudget?.versionHistory?.length
      ? activeBudget.versionHistory.reduce((max, v) =>
          v.version > max.version ? v : max,
        )
      : null;
    const undefinedCount =
      activeBudget && latestVersion !== null
        ? countUndefinedSuppliers(
            activeBudget,
            latestVersion.version,
            latestVersion.status,
          )
        : 0;

    if (undefinedCount > 0) {
      Swal.fire({
        title: "Fornecedor a Definir",
        text:
          undefinedCount === 1
            ? 'Não é possível enviar para Pré Fechamento enquanto existirem itens com o fornecedor "Fornecedor a Definir". Revise os fornecedores antes de prosseguir.'
            : `Foram encontrados ${undefinedCount} itens com o fornecedor "Fornecedor a Definir". Defina os fornecedores antes de enviar para Pré Fechamento.`,
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
      });
      return;
    }

    Swal.fire({
      title: "Enviar para Pré Fechamento?",
      text: "O fechamento ficará disponível para conciliação do Financeiro.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sim, enviar",
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed || !projectId || !activeBudgetId) return;
      if (closingApprovalTransitionRef.current) return;

      const success = await runTransition(() =>
        ClosingControlService.sendToPreClosing(projectId, activeBudgetId),
      );
      if (success) {
        Swal.fire({
          title: "Atualizado",
          text: "Fechamento enviado para Pré Fechamento.",
          icon: "success",
        });
      }
    });
  }, [projectId, activeBudgetId, activeBudget, runTransition]);

  const returnToOperation = useCallback(() => {
    Swal.fire({
      title: "Devolver para ajustes?",
      text: "O fechamento voltará para edição da Operação.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sim, devolver",
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed || !projectId || !activeBudgetId) return;

      const success = await runTransition(() =>
        ClosingControlService.returnClosing(projectId, activeBudgetId),
      );

      if (success) {
        Swal.fire({
          title: "Atualizado",
          text: "Fechamento devolvido para ajustes da Operação.",
          icon: "success",
        });
      }
    });
  }, [projectId, activeBudgetId, runTransition]);

  const handleClosingStageAction = useCallback(() => {
    if (closingApprovalTransitionRef.current) {
      return;
    }

    if (closingStageButton === "PRE_CLOSING") {
      void sendToPreClosing();
      return;
    }
    if (closingStageButton === "RETURN") {
      void returnToOperation();
    }
  }, [closingStageButton, returnToOperation, sendToPreClosing]);
  return {
    closingStatus: closingControl?.closingStatus ?? null,
    closingStatusLabel: closingControl?.closingStatusLabel ?? null,
    canEditClosing,
    closingStageButton,
    closingStageButtonLabel: closingStageButton
      ? getClosingStageButtonLabel(closingStageButton)
      : null,
    showClosingStageAction,
    handleClosingStageAction,
    isClosingControlPending,
  };
}
